/**
 * The Admin banner (docs/INTEGRATIONS.md "Data health"): at the top of every
 * page, while any webhook has gone quiet past its no-data alert, is switched
 * off with its alert on, is flooded, or is refusing what it gets — so an
 * Admin sees it without opening Settings. Computed when read — no job —
 * by the same verdict as System → Setup and /api/health/feeds. Not on the Webhooks pages themselves, which say it
 * already. Live: polled while visible (STATUS_VIEWS).
 */
import { Link } from '@tanstack/react-router'
import { BellRing } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useMinute } from '@/features/shared/hooks/use-minute'
import { whenText } from '@/lib/format'
import { useWebhookAlarmsQuery } from '../api'

export function FeedAlarmBanner({ enabled }: { enabled: boolean }) {
  const { data } = useWebhookAlarmsQuery(enabled)
  const now = useMinute()
  if (!enabled || !data || data.length === 0) return null
  const [first] = data
  const one = data.length === 1 && first
  return (
    <div
      role="status"
      data-testid="feed-alarm-banner"
      className="mb-6 flex flex-col gap-3 rounded-lg border-2 border-destructive/60 bg-destructive-surface px-4 py-3 md:flex-row md:items-center md:px-6"
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <BellRing className="mt-0.5 size-5 shrink-0 text-destructive-strong" aria-hidden="true" />
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-sm font-semibold">
            {one
              ? `Webhook “${one.name}” ${one.problem}`
              : `${data.length} webhooks need attention`}
          </span>
          <span className="max-w-measure text-sm text-muted-foreground">
            {one
              ? one.since
                ? `Nothing has arrived since ${whenText(one.since, now)}.`
                : 'Open it to see what is wrong and what to do.'
              : data.map((a) => `${a.name} ${a.problem}`).join('; ')}
          </span>
        </div>
      </div>
      <Button
        variant="outline"
        className="self-start md:shrink-0"
        nativeButton={false}
        role="link"
        render={<Link to="/settings/webhooks" search={one ? { webhook: one.id } : {}} />}
      >
        {one ? 'Open webhook' : 'Open webhooks'}
      </Button>
    </div>
  )
}
